import React, { useCallback, useState } from 'react';
import { Button, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { IconName, CustomIcon } from '../customIcon/CustomIcon';

export interface DropdownMenuOptionsProps<T> {
  label?: string;
  options: Array<DropdownMenuOption<T>>;
  onClick: (option: DropdownMenuOption<T>) => void;
  selectedItem?: DropdownMenuOption<T> | null;
  startIcon?: IconName;
  endIcon?: IconName;
}

export interface DropdownMenuOption<T> {
  label: string;
  value: T;
}

export const DropdownMenu = <T,>({
  label,
  options,
  onClick,
  selectedItem,
  startIcon,
  endIcon,
}: DropdownMenuOptionsProps<T>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOnClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl],
  );

  const handleOnClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const handleOnSelect = useCallback(
    (option: DropdownMenuOption<T>) => {
      handleOnClose();
      onClick(option);
    },
    [handleOnClose, onClick],
  );

  return (
    <Stack>
      <Button
        size="small"
        variant="text"
        qa-id="dropdown-menu-button"
        onClick={handleOnClick}
        startIcon={<CustomIcon icon={startIcon} />}
        endIcon={<CustomIcon icon={endIcon} />}
      >
        <Typography noWrap variant="subtitle1" color="text.primary">
          {selectedItem
            ? `${label ? label + ': ' : ''}${selectedItem.label}`
            : label}
        </Typography>
      </Button>
      <Menu
        qa-id="dropdown-menu"
        open={open}
        onClose={handleOnClose}
        anchorEl={anchorEl}
      >
        {options.map((option) => (
          <MenuItem
            key={option.label}
            sx={{ width: '226px' }}
            onClick={() => {
              handleOnSelect(option);
            }}
          >
            <Typography>{option.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
};
